import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { supabase } from '../services/supabase'
import { useAuth } from './AuthContext'

interface Product {
  id: string
  name: string
  price: number
  image_urls: string[]
  stock: number
}

interface WishlistItem {
  id: string
  customer_id: string
  product_id: string
  created_at: string
  product?: Product
}

interface WishlistContextType {
  wishlistItems: WishlistItem[]
  wishlistProductIds: Set<string>
  loading: boolean
  error: string | null
  wishlistCount: number
  addToWishlist: (productId: string) => Promise<{ success: boolean; error?: string }>
  removeFromWishlist: (productId: string) => Promise<{ success: boolean; error?: string }>
  toggleWishlist: (productId: string) => Promise<{ success: boolean; error?: string }>
  isInWishlist: (productId: string) => boolean
  refreshWishlist: () => Promise<void>
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined)

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const { user, session } = useAuth()
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create a Set of product IDs for O(1) lookup
  const wishlistProductIds = useMemo(
    () => new Set(wishlistItems.map(item => item.product_id)),
    [wishlistItems]
  )

  const wishlistCount = wishlistItems.length

  // Fetch wishlist items with product details
  const fetchWishlistItems = useCallback(async () => {
    if (!user || !session) {
      setWishlistItems([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch wishlist items
      const { data: wishlistData, error: wishlistError } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false })

      if (wishlistError) throw wishlistError

      if (!wishlistData || wishlistData.length === 0) {
        setWishlistItems([])
        return
      }

      // Fetch product details for each wishlist item
      const productIds = wishlistData.map(item => item.product_id)
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id, name, price, image_urls, stock')
        .in('id', productIds)

      if (productsError) throw productsError

      // Combine wishlist items with product data
      const itemsWithProducts = wishlistData.map(wishlistItem => ({
        ...wishlistItem,
        product: productsData?.find(p => p.id === wishlistItem.product_id)
      }))

      setWishlistItems(itemsWithProducts)
    } catch (err) {
      console.error('Error fetching wishlist items:', err)
      setError('Failed to load wishlist items')
    } finally {
      setLoading(false)
    }
  }, [user, session])

  // Fetch wishlist when user changes
  useEffect(() => {
    fetchWishlistItems()
  }, [fetchWishlistItems])

  // Add item to wishlist
  const addToWishlist = async (productId: string): Promise<{ success: boolean; error?: string }> => {
    if (!user) {
      return { success: false, error: 'Please sign in to add items to wishlist' }
    }

    try {
      const { error: insertError } = await supabase
        .from('wishlist_items')
        .insert({
          customer_id: user.id,
          product_id: productId
        })

      if (insertError) {
        // Handle unique constraint violation gracefully
        if (insertError.code === '23505') {
          return { success: true } // Already in wishlist
        }
        throw insertError
      }

      // Refresh wishlist
      await fetchWishlistItems()
      return { success: true }
    } catch (err) {
      console.error('Error adding to wishlist:', err)
      return { success: false, error: 'Failed to add item to wishlist' }
    }
  }

  // Remove item from wishlist
  const removeFromWishlist = async (productId: string): Promise<{ success: boolean; error?: string }> => {
    if (!user) {
      return { success: false, error: 'Please sign in to modify wishlist' }
    }

    try {
      const { error: deleteError } = await supabase
        .from('wishlist_items')
        .delete()
        .eq('customer_id', user.id)
        .eq('product_id', productId)

      if (deleteError) throw deleteError

      // Refresh wishlist
      await fetchWishlistItems()
      return { success: true }
    } catch (err) {
      console.error('Error removing from wishlist:', err)
      return { success: false, error: 'Failed to remove item from wishlist' }
    }
  }

  // Toggle wishlist (add if not present, remove if present)
  const toggleWishlist = async (productId: string): Promise<{ success: boolean; error?: string }> => {
    if (isInWishlist(productId)) {
      return removeFromWishlist(productId)
    } else {
      return addToWishlist(productId)
    }
  }

  // Check if product is in wishlist
  const isInWishlist = useCallback((productId: string): boolean => {
    return wishlistProductIds.has(productId)
  }, [wishlistProductIds])

  // Refresh wishlist manually
  const refreshWishlist = async () => {
    await fetchWishlistItems()
  }

  const value = {
    wishlistItems,
    wishlistProductIds,
    loading,
    error,
    wishlistCount,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    refreshWishlist
  }

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
}

export function useWishlist() {
  const context = useContext(WishlistContext)
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }
  return context
}
